import type { BiLogger } from '@wix/cli-telemetry';
import { unifiedAuthStrategy } from './auth-strategies/unified-auth-strategy.js';
import type {
  AuthStrategy,
  AuthStrategyState,
} from './auth-strategies/auth-strategy-types.js';
import { requestDeviceCode } from './device-code.js';
import {
  reportLoginFailure,
  reportLoginStart,
  reportLoginSuccess,
} from './login-bi.js';
import type { DeviceCodeResponse, UserInfo } from './schemas.js';
import { buildVerificationUris } from './verfication-uri.js';

export type LoginFlowMessage =
  | {
      email: string;
      event: 'logged_in' | 'success';
      userId: string;
    }
  | {
      event: 'awaiting_user';
      expiresInSeconds: number;
      userCode: string;
      verificationUri: string;
    };

export function emitLoginFlowMessage(event: LoginFlowMessage): void {
  // eslint-disable-next-line no-console
  console.log(`${JSON.stringify(event)}\n`);
}

export function createAwaitingUserLoginFlowMessage(
  deviceCode: DeviceCodeResponse
): LoginFlowMessage {
  const { coloredVerificationUri } = buildVerificationUris(
    deviceCode.verificationUri
  );

  return {
    event: 'awaiting_user',
    expiresInSeconds: deviceCode.expiresIn,
    userCode: deviceCode.userCode,
    verificationUri: coloredVerificationUri,
  };
}

export function createLoggedInLoginFlowMessage(
  userInfo: UserInfo
): LoginFlowMessage {
  return {
    email: userInfo.email,
    event: 'logged_in',
    userId: userInfo.userId,
  };
}

export function createSuccessfulLoginFlowMessage(
  userInfo: UserInfo
): LoginFlowMessage {
  return {
    email: userInfo.email,
    event: 'success',
    userId: userInfo.userId,
  };
}

export async function runAgentLoginFlow({
  biLogger,
  loginFn = unifiedAuthStrategy.login,
  signal = new AbortController().signal,
}: {
  biLogger: BiLogger;
  loginFn?: AuthStrategy['login'];
  signal?: AbortSignal;
}): Promise<AuthStrategyState> {
  reportLoginStart(biLogger);

  try {
    const deviceCode = await requestDeviceCode();

    emitLoginFlowMessage(createAwaitingUserLoginFlowMessage(deviceCode));

    const authData = await loginFn({
      deviceCode,
      signal,
    });

    reportLoginSuccess(biLogger, authData.userInfo);

    emitLoginFlowMessage(createSuccessfulLoginFlowMessage(authData.userInfo));

    return authData;
  } catch (error) {
    reportLoginFailure(biLogger, error);
    throw error;
  }
}
